// Onboarding step 1: interest areas. Areas are subjects, never stances
// (spec section 4): the skeptics who used to sit in lens pseudo-categories
// are reached through the subjects they publish on. Each area maps to
// expert category values; every category must appear in some area or
// nobody in it is ever suggested, and there is a test for that. Rewritten
// 28 Jul 2026 with the consolidated category vocabulary (42 drifted labels
// became 16 subjects; experts can carry several).
export const AREAS: Array<[id: string, label: string, emoji: string, cats: string[]]> = [
  ["sleep", "Sleep", "😴", ["Sleep"]],
  ["exercise", "Exercise and training", "🏋", ["Exercise"]],
  ["food", "Food and nutrition", "🥗", ["Nutrition"]],
  // Shortened 1 Aug 2026: "Blood sugar, heart and metabolism" clipped under
  // the tile's ellipsis, and metabolism is what those two things are.
  ["metabolic", "Blood sugar and heart", "🔥", ["Metabolic & heart"]],
  ["longevity", "Ageing well", "⏳", ["Longevity"]],
  ["exposure", "Chemicals and exposures", "🧴", ["Exposures", "Skin"]],
  ["habits", "Habits that stick", "🔁", ["Habits"]],
  ["mind", "Mood and mind", "🧠", ["Mind"]],
  ["connection", "Meaning and connection", "🤝", ["Meaning & connection"]],
  ["mortality", "Meaning and mortality", "🕯", ["Meaning & connection"]],
  ["women", "Women's health", "🌸", ["Women's health", "Sexual health"]],
  ["men", "Men's health", "🧔", ["Men's health", "Sexual health"]],
  ["sex", "Sex and intimacy", "💗", ["Sexual health"]],
  ["family", "Kids and parents", "🧸", ["Family health"]],
  ["mouth", "Teeth and mouth", "🦷", ["Oral health"]],
  ["prevention", "Vaccines and public health", "🛡", ["Prevention"]],
];

/* Which topic filters an area opens (1 Aug 2026, Rob). Ten of the eleven
   filterable topics start off, which made the old step 2 a wall of switches
   in front of someone who did not yet know what the product was. The filters
   now sit collapsed under step 1 and the areas seed them, on this rule: if
   the area is the subject, the reader gets the subject whole, contested
   edges included. A filter stays off only when it is genuinely a different
   topic, not a spicier corner of the same one. So Food opens extended
   fasting and restrictive diets, Exposures opens detox, Kids and parents
   opens vaccines: hiding those would make the area a lie about what the
   people you follow actually publish.

   This seeds, it does not own. `touched` in the store records every switch
   the reader moved by hand, and a touched switch is never moved by an area
   again (store.ts, seedTopics). Untick an area and its untouched seeds go
   back off, which is what makes the panel safe to leave closed.

   Keys are area ids, values are topic ids from TOPICS; both sides are
   covered by a test, so a typo here cannot ship silently. */
export const AREA_TOPICS: Record<string, string[]> = {
  sleep: ["supplements"],
  exercise: ["supplements", "hormones", "screening"],
  food: ["longfast", "restrictive", "supplements"],
  metabolic: ["longfast", "restrictive", "supplements", "screening"],
  longevity: ["longfast", "supplements", "hormones", "screening"],
  exposure: ["detox", "screening"],
  habits: ["mental"],
  mind: ["mental", "supplements"],
  connection: ["mortality", "mental"],
  mortality: ["mortality", "mental"],
  women: ["hormones", "sex", "screening", "supplements"],
  men: ["hormones", "sex", "screening", "supplements"],
  sex: ["sex", "hormones", "supplements"],
  family: ["vaccines", "screening"],
  // Teeth and mouth is the one area with no contested edge. An honest zero
  // beats an invented link.
  mouth: [],
  prevention: ["vaccines", "screening"],
};

/* The topic ids the ticked areas open, in AREAS order. */
export function areaTopics(areas: Record<string, boolean>): string[] {
  const ids: string[] = [];
  for (const [id] of AREAS) {
    if (!areas[id]) continue;
    for (const t of AREA_TOPICS[id] ?? []) if (!ids.includes(t)) ids.push(t);
  }
  return ids;
}

export function areaCats(areas: Record<string, boolean>): string[] {
  const cats: string[] = [];
  for (const [id, , , areaCatList] of AREAS) {
    if (!areas[id]) continue;
    for (const c of areaCatList) if (!cats.includes(c)) cats.push(c);
  }
  return cats;
}

// The other direction: the areas an expert's categories fall in. Used when a
// reader enters the flow from one person's page (30 Jul 2026), so step 1 opens
// with that person's subjects already ticked rather than asking them to guess
// which of fourteen rows the page they just read belongs to. Every tick is
// still theirs to undo, and picking at least one is still forced.
export function catsToAreas(cats: string[]): string[] {
  const has = new Set(cats);
  return AREAS.filter(([, , , areaCatList]) => areaCatList.some((c) => has.has(c))).map(([id]) => id);
}
